import { Controller } from "@hotwired/stimulus";
import { MDCSelect } from "@material/select";
import compactNumber from "../lib/compact_number";
import { deleteChartValue, duplicateYearIndex, normalizeValues, saveChartValue, submitDisabled } from "../lib/chart_state.mjs";

export default class extends Controller {
  static targets = ["chart", "barTemplate", "newForm", "newButton", "formTitle", "submitLabel", "submitButton", "deleteButton", "error", "status"];
  static values = { translations: Object };

  connect() {
    this.editIndex = null;
    this.form = this.element.closest("form");
    this.handleSubmit = () => this.showStatus(this.text("saving"));
    this.yearSelectElement = this.yearInput.closest(".mdc-select");
    this.handleYearSelectChange = (event) => {
      this.yearInput.value = event.detail.value;
      this.updateSubmitState();
    };
    this.form?.addEventListener("submit", this.handleSubmit);
    this.yearSelectElement?.addEventListener("MDCSelect:change", this.handleYearSelectChange);
    this.renderChart();
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.handleSubmit);
    this.yearSelectElement?.removeEventListener("MDCSelect:change", this.handleYearSelectChange);
  }

  editChartItem(event) {
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;

    const bar = event.target.closest(".bar[data-index]");
    if (bar == null) return;

    event.preventDefault();
    this.editIndex = Number(bar.dataset.index);
    const [number, year] = this.values()[this.editIndex];
    this.numberInput.value = number;
    this.setYear(year);
    this.initialEditorValue = this.editorValue();
    this.formTitleTarget.textContent = this.text("edit_title");
    this.submitLabelTarget.textContent = this.text("update");
    this.submitButtonTarget.disabled = true;
    this.deleteButtonTarget.classList.remove("hidden");
    this.showForm();
    this.renderChart();
  }

  showNewForm() {
    this.editIndex = null;
    this.numberInput.value = "";
    this.setYear("");
    this.formTitleTarget.textContent = this.text("add_title");
    this.submitLabelTarget.textContent = this.text("add");
    this.submitButtonTarget.disabled = true;
    this.deleteButtonTarget.classList.add("hidden");
    this.showForm();
    this.renderChart();
  }

  cancelEdit() {
    this.closeForm();
    this.renderChart();
  }

  updateSubmitState() {
    this.submitButtonTarget.disabled = submitDisabled(this.numberInput.value, this.yearInput.value, this.editIndex, this.editorValue(), this.initialEditorValue);
  }

  addChartItem() {
    const number = Number(this.numberInput.value);
    const year = Number(this.yearInput.value);

    if (!Number.isInteger(number) || number < 1) {
      this.showError(this.text("invalid_number"));
      return;
    }

    if (!Number.isInteger(year)) {
      this.showError(this.text("invalid_year"));
      return;
    }

    const values = this.values();
    const existingIndex = duplicateYearIndex(values, year);
    if (existingIndex >= 0 && existingIndex !== this.editIndex) {
      this.showError(this.text("duplicate_year"));
      return;
    }
    this.saveValues(saveChartValue(values, this.editIndex, number, year));
    this.closeForm();
    this.renderChart();
  }

  deleteChartItem() {
    if (this.editIndex == null) return;

    this.saveValues(deleteChartValue(this.values(), this.editIndex));
    this.closeForm();
    this.renderChart();
  }

  renderChart() {
    const values = this.values();
    const max = Math.max(...values.map((item) => Number(item[0])), 1);
    this.chartTarget.innerHTML = "";

    if (values.length === 0) {
      const empty = document.createElement("p");
      empty.className = "label-hint chart-empty-state";
      empty.textContent = this.text("empty");
      this.chartTarget.appendChild(empty);
      return;
    }

    values.forEach(([number, year], index) => {
      const fragment = this.barTemplateTarget.content.cloneNode(true);
      const bar = fragment.querySelector(".bar");
      bar.dataset.index = index;
      bar.style.height = `${100 * Number(number) / max}%`;
      bar.setAttribute("aria-label", this.text("bar_label", { year, number }));
      bar.classList.toggle("selected", index === this.editIndex);
      fragment.querySelector("input[data-field='year']").value = year;
      fragment.querySelector("input[data-field='number']").value = number;
      fragment.querySelector(".number").textContent = compactNumber(number);
      fragment.querySelector(".year").textContent = year;
      this.chartTarget.appendChild(fragment);
    });
  }

  values() {
    const values = JSON.parse(this.chartTarget.dataset.values || "[]");
    return normalizeValues(values);
  }

  saveValues(values) {
    this.chartTarget.dataset.values = JSON.stringify(values);
    this.showStatus(this.text("unsaved"), true);
    this.form?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  showForm() {
    this.hideError();
    this.newButtonTarget.classList.add("hidden");
    this.newFormTarget.classList.remove("hidden");
    this.numberInput.focus();
  }

  closeForm() {
    this.editIndex = null;
    this.hideError();
    this.newButtonTarget.classList.remove("hidden");
    this.newFormTarget.classList.add("hidden");
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  showStatus(message, unsaved = false) {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("chart-save-status--unsaved", unsaved);
    this.statusTarget.classList.remove("hidden");
  }

  hideError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  setYear(year) {
    this.yearInput.value = year;
    const selectElement = this.yearInput.closest(".mdc-select");
    if (selectElement == null) return;

    const select = selectElement.MDCSelect || new MDCSelect(selectElement);
    select.value = year;
  }

  get numberInput() {
    return this.newFormTarget.querySelector("#number");
  }

  get yearInput() {
    return this.newFormTarget.querySelector("#year");
  }

  editorValue() {
    return `${this.numberInput.value}:${this.yearInput.value}`;
  }

  text(key, replacements = {}) {
    return Object.entries(replacements).reduce((text, [name, value]) => text.replace(`%{${name}}`, value), this.translationsValue[key]);
  }

}
